import React from "react";

import { FormControlLabel, Switch, Typography, Table, TableBody, TableRow, TableCell, Accordion, AccordionSummary, AccordionDetails, TextField, TableHead, TableContainer } from "@mui/material";
import { Box, Divider, RadioGroup, Radio, Stack, Button } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { TestCase } from "../../types/TestCase";
import { TestSupportStatus, WorkshopTestCase } from "../../types/WorkshopTestCases";
import { WorkshopTestResult, WorkshopTestCaseResult } from "../../types/WorkshopResult";
import { FieldType, Interface } from "../../types/Interface";
import { UnsupportedChip } from "./UnsupportedChip";
import { copyArrayWithIndexChanged } from '../../Utils';

interface CommentFieldProps {
    value?: string | null,
    disabled: boolean,
    commentOnChangeHandler: (value: string) => void
}

interface TestCaseGroupProps {
    supported: boolean,
    testCase: TestCase,
    clientWSTestcase: WorkshopTestCase,
    serverWSTestcase: WorkshopTestCase,
    workshopTestCaseResult: WorkshopTestCaseResult
}

interface TestCaseGroupData {
    supportedCase: boolean,
    groupName: string,
    testProps: TestCaseGroupProps[]
}

const GroupTestCaseSupport = (testCases: TestCase[], clientWSTestcases: WorkshopTestCase[],
    serverWSTestcases: WorkshopTestCase[], workshopTestCaseResults: WorkshopTestCaseResult[]) => {
    let testCaseGroups: TestCaseGroupData[] = [];
    try {
        let testCaseGroupData: TestCaseGroupData;
        let testCaseGroupProp: TestCaseGroupProps;
        let index = 0;

        testCases.forEach(testCase => {
            index = testCaseGroups.findIndex(tc => tc.groupName === testCase.group);
            testCaseGroupData = testCaseGroups.find(tc => tc.groupName === testCase.group) ??
            {
                supportedCase: false,
                groupName: testCase.group,
                testProps: []
            };
            testCaseGroupProp = {
                supported: false,
                testCase: testCase,
                clientWSTestcase: clientWSTestcases.find(wstc => wstc.testCaseId === testCase.id) ??
                    { workshopProductId: 0, testCombinationId: 0, testCaseId: testCase.id, supportStatus: TestSupportStatus.Unsupported},
                serverWSTestcase: serverWSTestcases.find(wstc => wstc.testCaseId === testCase.id) ??
                    { workshopProductId: 0, testCombinationId: 0, testCaseId: testCase.id, supportStatus: TestSupportStatus.Unsupported },
                workshopTestCaseResult: workshopTestCaseResults.find(wstcr => wstcr.testCaseId === testCase.id) ??
                    { guid: '', comment: '', value: null, result: WorkshopTestResult.ClientNotSupported, testCaseId: testCase.id }
            };
            testCaseGroupProp.supported = (testCaseGroupProp.clientWSTestcase.supportStatus === TestSupportStatus.Supported ||
                testCaseGroupProp.clientWSTestcase.supportStatus === TestSupportStatus.ClientSupported) &&
                (testCaseGroupProp.serverWSTestcase.supportStatus === TestSupportStatus.Supported ||
                testCaseGroupProp.serverWSTestcase.supportStatus === TestSupportStatus.ServerSupported);
            if (testCaseGroupData.supportedCase === false) testCaseGroupData.supportedCase = testCaseGroupProp.supported;
            testCaseGroupData.testProps.push(testCaseGroupProp);
            testCaseGroups = copyArrayWithIndexChanged(testCaseGroups, index, testCaseGroupData);
        })
    } catch (e) {
        console.error("Error while grouping test cases:", e);
        // Handle error appropriately, e.g., show a notification or log it
        return [] as TestCaseGroupData[]; // Return an empty array if there's an error
    }
    return testCaseGroups;
}

export const GroupedTestCases2Accordion = ({ activeInterface, testCases, clientWSTestcases, serverWSTestcases, workshopTestCaseResults, disabledForm, changeTestCaseResultHandler }:
    {
        activeInterface: Interface | undefined,
        testCases: TestCase[],
        clientWSTestcases: WorkshopTestCase[],
        serverWSTestcases: WorkshopTestCase[],
        workshopTestCaseResults: WorkshopTestCaseResult[],
        disabledForm?: boolean,
        changeTestCaseResultHandler: (tstCase: TestCase, wstcr: WorkshopTestCaseResult) => void
    }) => {
    const [filterTestCases, setFilterTestCases] = React.useState<boolean>(true);
    const [groupedTestCases, setGroupedTestCases] = React.useState<TestCaseGroupData[]>([]);

    React.useEffect(() => {
        const groupedTestCasesLoc = GroupTestCaseSupport(testCases, clientWSTestcases, serverWSTestcases, workshopTestCaseResults);
        setGroupedTestCases(groupedTestCasesLoc);
    }, [testCases, clientWSTestcases, serverWSTestcases, workshopTestCaseResults]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFilterTestCases(event.target.checked);
    };

    return <form>
        <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack width={'100%'} direction={'row'} justifyContent={'space-between'}>
                        <Typography variant='bold'>{activeInterface?.name}</Typography>
                        <FormControlLabel label="Filter Unsupported" control={<Switch checked={filterTestCases}
                            onChange={handleChange} inputProps={{ 'aria-label': 'controlled' }} />} />
                    </Stack>
                </AccordionSummary>
                <AccordionDetails>
                {
                    groupedTestCases.map(group => (!filterTestCases || (filterTestCases && group.supportedCase)) &&
                        <TestCaseGroupAccordion
                            testCaseGroupData={group} disabledForm={disabledForm ?? false} filterTestCases={filterTestCases}
                            changeTestCaseResultHandler={changeTestCaseResultHandler} />)
                    }
                </AccordionDetails>
            </Accordion>
        </form>
}

const TestCaseGroupAccordion = ({ testCaseGroupData, disabledForm, filterTestCases, changeTestCaseResultHandler }:
    {
        testCaseGroupData: TestCaseGroupData,
        disabledForm: boolean,
        filterTestCases: boolean,
        changeTestCaseResultHandler: (tstCase: TestCase, wstcr: WorkshopTestCaseResult) => void
    }) => {
    return <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{testCaseGroupData.groupName}</AccordionSummary>
            <AccordionDetails>
            <TestCaseGroupDetails testCaseGroupData={testCaseGroupData} disabledForm={disabledForm}
                filterTestCases={filterTestCases} changeTestCaseResultHandler={changeTestCaseResultHandler} />
            </AccordionDetails>
    </Accordion>
}

const TestCaseGroupDetails = ({ testCaseGroupData, disabledForm, filterTestCases, changeTestCaseResultHandler }:
    {
        testCaseGroupData: TestCaseGroupData,
        disabledForm: boolean,
        filterTestCases: boolean,
        changeTestCaseResultHandler: (tstCase: TestCase, wstcr: WorkshopTestCaseResult) => void
    }) => {

    return <>
        {testCaseGroupData && <fieldset className='disabledForm' disabled={disabledForm}>
            <Table className={'alternating'}>
                <TableBody>
                    {
                        testCaseGroupData.testProps.map(tc =>
                            (!filterTestCases || (filterTestCases && tc.supported)) && 
                            <TestCaseContainer testCaseGroupProps={tc} changeTestCaseResultHandler={changeTestCaseResultHandler} />)
                        
                    }
                </TableBody>
            </Table>
        </fieldset>}
    </>
}

const TestCaseContainer = ({ testCaseGroupProps, changeTestCaseResultHandler }:
    {
        testCaseGroupProps: TestCaseGroupProps,
        changeTestCaseResultHandler: (tstCase: TestCase, wstcr: WorkshopTestCaseResult) => void
    }) => {
    const [expanded, setExpanded] = React.useState<boolean>(false);

    const handleAccordionChange =
        () => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded);
        };

    return <TableRow>
        <TableCell width=''>
            {testCaseGroupProps.testCase.description ?
                <AccordionDetails>
                    <Accordion defaultExpanded={false} onChange={handleAccordionChange()} sx={{ bgcolor: 'inherit' }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Stack width={'100%'} spacing={ 10} direction={'row'} justifyContent={'space-between'}>
                                <Box width={'90%'}> <Typography variant='body2'>{testCaseGroupProps.testCase.name}</Typography> </Box>
                                <Divider orientation="vertical" flexItem />
                                <Typography variant='subscript'>{expanded ? 'Hide description' : 'Show description'}</Typography>
                            </Stack>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Divider />
                            {testCaseGroupProps.testCase.description}
                            <Divider />
                        </AccordionDetails>
                    </Accordion>
                </AccordionDetails>
                :
                testCaseGroupProps.testCase.name}
        </TableCell>
        <TableCell width='50%' padding='none' sx={{ borderLeft: '1px solid', borderColor: '#e1e9ee' }}>
            {<TestingResultInput testCaseGroupProps={testCaseGroupProps} changeHandler={changeTestCaseResultHandler} />}
        </TableCell>
    </TableRow>
}

const TestingResultInput = ({ testCaseGroupProps, changeHandler }:
    {
        testCaseGroupProps: TestCaseGroupProps,
        changeHandler: (testCase: TestCase, wtcr: WorkshopTestCaseResult) => void
    }) => {
    if (testCaseGroupProps.clientWSTestcase && testCaseGroupProps.serverWSTestcase) {
        // check if there is already a result for this test case, and correct if necessary
        // this is because if the user goes back to the config page and changes the support status, the result should be reset
        if (testCaseGroupProps.workshopTestCaseResult) {
            let serverSupported = testCaseGroupProps.serverWSTestcase.supportStatus === TestSupportStatus.Supported;
            let clientSupported = testCaseGroupProps.clientWSTestcase.supportStatus === TestSupportStatus.Supported;

            if (!serverSupported || !clientSupported) {
                // if the test case is not supported by the server or client, the result should be set to the corresponding not supported value
                if (testCaseGroupProps.workshopTestCaseResult.result !== (!serverSupported ? WorkshopTestResult.ServerNotSupported : WorkshopTestResult.ClientNotSupported)) {
                    testCaseGroupProps.workshopTestCaseResult.result = (!serverSupported) ? WorkshopTestResult.ServerNotSupported : WorkshopTestResult.ClientNotSupported;
                    testCaseGroupProps.workshopTestCaseResult.comment = '';
                    testCaseGroupProps.workshopTestCaseResult.value = null;
                }
            }
            else if (serverSupported && testCaseGroupProps.workshopTestCaseResult.result === WorkshopTestResult.ServerNotSupported) {
                // if the test was previously marked as not supported, but now is supported, the result should be reset
                testCaseGroupProps.workshopTestCaseResult.result = WorkshopTestResult.NotComplete;
            }
            else if (clientSupported && testCaseGroupProps.workshopTestCaseResult.result === WorkshopTestResult.ClientNotSupported) {
                // if the test was previously marked as not supported, but now is supported, the result should be reset
                testCaseGroupProps.workshopTestCaseResult.result = WorkshopTestResult.NotComplete;
            }
        }
    }
    else {
        // if there is no support status, a response is not possible
        return <></>;
    }

    let resultRadio = <RadioGroupInput testCase={testCaseGroupProps.testCase} workshopTestCaseResult={testCaseGroupProps.workshopTestCaseResult} changeHandler={changeHandler} />
    let optionalResultInput = <></>

    const commentDisabled = testCaseGroupProps.workshopTestCaseResult?.result === WorkshopTestResult.ServerNotSupported ||
        testCaseGroupProps.workshopTestCaseResult?.result === WorkshopTestResult.ClientNotSupported

    const commentOnChangeHandler = (comment: string) => {
        changeHandler(testCaseGroupProps.testCase, { ...testCaseGroupProps.workshopTestCaseResult as WorkshopTestCaseResult, comment: comment ?? '' })
    }

    if (testCaseGroupProps.workshopTestCaseResult?.result === WorkshopTestResult.ServerNotSupported)
        resultRadio = <UnsupportedChip server={true} />
    else if (testCaseGroupProps.workshopTestCaseResult?.result === WorkshopTestResult.ClientNotSupported)
        resultRadio = <UnsupportedChip server={false} />
    else {
        switch (testCaseGroupProps.testCase.fieldType) {
            case FieldType.Table: optionalResultInput = <TableInput testCase={testCaseGroupProps.testCase} workshopTestCaseResult={testCaseGroupProps.workshopTestCaseResult}
                changeHandler={changeHandler} />
                break;
        }
    }

    return <Stack direction='column'>
        <Stack direction='row' justifyContent={'space-evenly'} padding={'5px'} paddingLeft={'10px'}>
            {resultRadio}
        </Stack>
        <CommentField value={testCaseGroupProps.workshopTestCaseResult?.comment} disabled={commentDisabled}
            commentOnChangeHandler={commentOnChangeHandler} />
        {optionalResultInput}
    </Stack>
}

const RadioGroupInput = ({ testCase, workshopTestCaseResult, changeHandler }:
    { testCase: TestCase, workshopTestCaseResult?: WorkshopTestCaseResult, changeHandler: (testCase: TestCase, wtcr: WorkshopTestCaseResult) => void }) => {

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        // If Reset selected, set result to NotComplete and the parent will update the model.
        if (newValue === 'reset') {
            changeHandler(testCase, { ...workshopTestCaseResult, result: WorkshopTestResult.NotComplete } as WorkshopTestCaseResult);
            return;
        }

        changeHandler(testCase, { ...workshopTestCaseResult, result: Number(newValue) } as WorkshopTestCaseResult)
    };

    return <RadioGroup row={true} sx={{ flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'inherit' }} value={workshopTestCaseResult?.result ?? 1337} //dummy, so value is not undefined during the first render
        onChange={handleChange}>
        <FormControlLabel value={WorkshopTestResult.Passed} sx={{ flexShrink: 1 }}
            control={<Radio sx={{ '&.Mui-checked': { color: 'green' } }} />}
            label={<Typography variant='body2' sx={workshopTestCaseResult?.result === WorkshopTestResult.Passed ? { color: 'green' } : {}}>{"Passed"}</Typography>} />

        <FormControlLabel value={WorkshopTestResult.Warning} sx={{ flexShrink: 1 }}
            control={<Radio sx={{ '&.Mui-checked': { color: 'orange' } }} />}
            label={<Typography variant='body2' sx={workshopTestCaseResult?.result === WorkshopTestResult.Warning ? { color: 'orange' } : {}}>{"Warning"}</Typography>} />

        <FormControlLabel value={WorkshopTestResult.Failed} sx={{ flexShrink: 1 }}
            control={<Radio sx={{ '&.Mui-checked': { color: 'red' } }} />}
            label={<Typography variant='body2' sx={workshopTestCaseResult?.result === WorkshopTestResult.Failed ? { color: 'red' } : {}}>{"Failed"}</Typography>} />

        {/* Minimal-change Reset: when clicked, set model to NotComplete. Parent update makes value 0 so no radio is selected. */}
        <FormControlLabel value={'reset'} sx={{ flexShrink: 1 }}
            control={<Radio />}
            label={<Typography variant='body2'>{"Reset"}</Typography>} />
    </RadioGroup>
}

const CommentField = ({ value, disabled, commentOnChangeHandler }: CommentFieldProps) => {
    const [textFieldContents, setTextFieldContents] = React.useState('');
    React.useEffect(() => {
        setTextFieldContents(value ?? '');
    }, [value]);

    return <TextField fullWidth sx={{ flexGrow: 1, display: textFieldContents === "" && disabled ? "none" : "inline" }}
        label='Comment' disabled={disabled} multiline value={textFieldContents}
        onChange={e => setTextFieldContents(e.target.value)} onBlur={e => commentOnChangeHandler(textFieldContents)} />
}

const TableInput = ({ testCase, workshopTestCaseResult, changeHandler }:
    { testCase: TestCase, workshopTestCaseResult?: WorkshopTestCaseResult, changeHandler: (testCase: TestCase, wtcr: WorkshopTestCaseResult) => void }) => {
    let columnNames;
    let tableRows;

    try {
        columnNames = JSON.parse(testCase.fieldSchema)['columns'];
        tableRows = JSON.parse(workshopTestCaseResult?.value ?? '[\"\"]');
    } catch (e) {
        return <>{(e as SyntaxError).message}</>;
    }

    return <TableContainer>
        <InputTable columnNames={columnNames} rows={tableRows} />
        <Button onClick={() => { }}>Add row</Button>
    </TableContainer>
}

const InputTable = ({ columnNames, rows }: { columnNames?: [string], rows?: [[string]] }) => {
    return <Table>
        <TableHead>
            <TableRow>
                {columnNames?.map(columnName => <TableCell>{columnName}</TableCell>)}
            </TableRow>
        </TableHead>
        <TableBody>
            {rows && rows?.map(row => <TableRow>{row.map?.(column => <TableCell>{column}</TableCell>)} </TableRow>)}
        </TableBody>
    </Table>
}
